import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  accessTokenAtom,
  assignmentTokenAtom,
  authErrorAtom,
  authErrorCodeAtom,
  authInitializingAtom,
  authStateAtom,
  authStatusAtom,
  invitationDataAtom,
  invitationLoadingAtom,
  isAcceptChallengeDialogOpenAtom,
  isLoginDialogOpenAtom,
  isSignupDialogOpenAtom,
  logoutAtom,
  organizationTypeAtom,
  setAuthErrorAtom,
  setUserAtom,
  userDisplayNameAtom,
  userInitialsAtom,
} from '../store/userAtoms'
import { apis } from '../modules/apiClients'
import { AxiosError } from 'axios'
import { UserService } from '@duck/api-definitions'
import { toast } from 'sonner'
import { isAdmin, isPartner } from '../utils/roleGuard'
import { useTar } from '@/modules'
import { useNavigate } from '@tanstack/react-router'
import { identifyUserInSentry, clearUserInSentry } from '@/instrument.sentry'
import { queryClient } from '@/modules/queryClient'
import { ClientAnalytics } from '@/modules/clientAnalytics'

const initializationRef: { current: Promise<void> | null } = { current: null }

const identifySession = (user: UserService.User) => {
  identifyUserInSentry(user)
  ClientAnalytics.identify(user)
}

export const useAuth = () => {
  const { t } = useTar()
  const authState = useAtomValue(authStateAtom)
  const isLoggedIn = authState.isAuthenticated
  const setUser = useSetAtom(setUserAtom)
  const setAccessToken = useSetAtom(accessTokenAtom)
  const accessToken = useAtomValue(accessTokenAtom)
  const setAuthStatus = useSetAtom(authStatusAtom)
  const setError = useSetAtom(setAuthErrorAtom)
  // Local loading states for each auth function
  const [isSigningInWithGoogle, setIsSigningInWithGoogle] = useState(false)
  const [, setIsSigningInWithLinkedIn] = useState(false)
  const [isSigningInWithEmail, setIsSigningInWithEmail] = useState(false)
  const [isRegistering, setIsRegistering] = useState(false)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [isInitializing, setIsInitializing] = useAtom(authInitializingAtom)
  const error = useAtomValue(authErrorAtom)
  const errorCode = useAtomValue(authErrorCodeAtom)
  const logout = useSetAtom(logoutAtom)
  const displayName = useAtomValue(userDisplayNameAtom)
  const initials = useAtomValue(userInitialsAtom)
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useAtom(
    isLoginDialogOpenAtom
  )
  const [isSignupDialogOpen, setIsSignupDialogOpen] = useAtom(
    isSignupDialogOpenAtom
  )
  const [invitationData, setInvitationData] = useAtom(invitationDataAtom)
  const [invitationLoading, setInvitationLoading] = useAtom(invitationLoadingAtom)
  const [isAcceptChallengeDialogOpen, setIsAcceptChallengeDialogOpen] = useAtom(isAcceptChallengeDialogOpenAtom)
  const [assignmentToken, setAssignmentToken] = useAtom(assignmentTokenAtom)
  const [isJoiningAssignment, setIsJoiningAssignment] = useState(false)
  const navigate = useNavigate()
  const signingInRef = useRef(false)
  const hasProcessedStoredAssignmentTokenRef = useRef(false)

  const signInWithGoogle = useCallback(async () => {
    try {
      // Prevent multiple simultaneous Google sign-in attempts
      if (signingInRef.current) {
        console.warn('Google sign-in already in progress, ignoring duplicate request')
        return
      }

      signingInRef.current = true
      setIsSigningInWithGoogle(true)
      setAuthStatus('loading')
      setError(null)

      // Store current location to redirect back after auth
      localStorage.setItem('auth_redirect_url', window.location.pathname)

      // Persist assignment token across OAuth redirects so we can resume signup flow
      if (assignmentToken) {
        localStorage.setItem('assignmentToken', assignmentToken)
      }

      // Add a small delay to prevent rapid-fire clicks
      await new Promise(resolve => setTimeout(resolve, 100))

      // Redirect to backend Google OAuth endpoint
      // Note: assignmentToken is only passed for NEW user registration (sign-up flow).
      // For existing users (sign-in), the token in localStorage is handled after sign-in completes.
      const googleUrl = new URL(`${window.location.origin}/api/users/auth/google`)
      if (assignmentToken) {
        googleUrl.searchParams.set('assignmentToken', assignmentToken)
      }
      window.location.href = googleUrl.toString()

    } catch (error) {
      console.error('Google sign-in error:', error)
      setError((error as Error).message || 'Failed to sign in with Google. Please try again, matey!')
      setAuthStatus('error')
      setIsSigningInWithGoogle(false)
    } finally {
      signingInRef.current = false
    }
  }, [setIsSigningInWithGoogle, setAuthStatus, setError, assignmentToken])

  const signInWithLinkedIn = useCallback(async () => {
    try {
      // Prevent multiple simultaneous LinkedIn sign-in attempts
      if (signingInRef.current) {
        console.warn('LinkedIn sign-in already in progress, ignoring duplicate request')
        return
      }

      signingInRef.current = true
      setIsSigningInWithLinkedIn(true)
      setAuthStatus('loading')
      setError(null)

      // Store current location to redirect back after auth
      localStorage.setItem('auth_redirect_url', window.location.pathname)

      // Persist assignment token across OAuth redirects so we can resume signup flow
      if (assignmentToken) {
        localStorage.setItem('assignmentToken', assignmentToken)
      }

      // Add a small delay to prevent rapid-fire clicks
      await new Promise(resolve => setTimeout(resolve, 100))

      // Redirect to backend LinkedIn OAuth endpoint
      // Note: assignmentToken is only passed for NEW user registration (sign-up flow).
      // For existing users (sign-in), the token in localStorage is handled after sign-in completes.
      const linkedInUrl = new URL(`${window.location.origin}/api/users/auth/linkedin`)
      if (assignmentToken) {
        linkedInUrl.searchParams.set('assignmentToken', assignmentToken)
      }
      window.location.href = linkedInUrl.toString()

    } catch (error) {
      console.error('LinkedIn sign-in error:', error)
      setError((error as Error).message || 'Failed to sign in with LinkedIn. Please try again, matey!')
      setAuthStatus('error')
      setIsSigningInWithLinkedIn(false)
    } finally {
      signingInRef.current = false
    }
  }, [setIsSigningInWithLinkedIn, setAuthStatus, setError, assignmentToken])

  const signInWithEmail = useCallback(
    async (credentials: UserService.LoginRequest) => {
      try {
        setIsSigningInWithEmail(true)
        setAuthStatus('loading')
        setError(null)


        // Basic validation
        if (!credentials.email || !credentials.password) {
          throw new Error('Email and password are required')
        }
        try {
          const { data } = await apis.userService.authLoginPost(credentials)
          const { user, accessToken, organizationType } = data

          setUser(user, organizationType)
          setAccessToken(accessToken)
          setIsLoginDialogOpen(false) // Close dialog on successful sign-in

          identifySession(user)

          // Server sets refresh token in HTTP-only cookies, access token stored in atom! ⭐🏴‍☠️
        } catch (error) {
          if (error instanceof AxiosError && error.response?.data) {
            const errorData = error.response.data
            setError({
              error: errorData.error || 'An unknown error occurred',
              errorCode: errorData.errorCode
            })
          } else {
            setError('An unknown error occurred')
          }
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'An unknown error occurred'
        setError(`Failed to sign in: ${errorMessage}`)
      } finally {
        setIsSigningInWithEmail(false)
      }
    },
    [setUser, setIsSigningInWithEmail, setAuthStatus, setError, setIsLoginDialogOpen]
  )

  const register = useCallback(
    async (registrationData: UserService.RegisterRequest) => {
      try {
        setIsRegistering(true)
        setAuthStatus('loading')
        setError(null)

        // Basic validation
        if (!registrationData.email || !registrationData.password) {
          throw new Error('Email and password are required')
        }

        try {
          const { data: { email, result, authResponse } } = await apis.userService.rootPost(registrationData)

          if (result === 'completed' && authResponse) {
            const organizationType = authResponse.organizationType
            setUser(authResponse.user, organizationType)
            setAccessToken(authResponse.accessToken)

            identifySession(authResponse.user)

            toast.success(t('system.registrationComplete'))
            if (authResponse.user.role === UserService.UserRole.Partner) {
              navigate({ to: '/partner/dashboard' })
            } else if (authResponse.user.personId) {
              navigate({ to: '/assignments' })
            } else {
              navigate({ to: '/challenges/available' })
            }
          } else {
            toast.success(t('system.registrationCompleteCheckEmail', { email }), {
              duration: Infinity,
              action: {
                label: t('common.close'),
                onClick: () => { /* dismiss handled by sonner */ }
              }
            })
            navigate({ to: '/' })
          }
        } catch (apiError) {
          setError(
            apiError instanceof AxiosError
              ? apiError.response?.data.error
              : 'An unknown error occurred during registration'
          )
          toast.error(t('errors.registrationFailed'))
        }
        setError(null)
        setIsSignupDialogOpen(false)
      } catch (generalError) {
        const errorMessage =
          generalError instanceof Error ? generalError.message : 'An unknown error occurred'
        setError(`Failed to register: ${errorMessage}`)
      } finally {
        setIsRegistering(false)
      }
    },
    [setUser, setIsRegistering, setAuthStatus, setError, setIsSignupDialogOpen]
  )

  const signOut = useCallback(async () => {
    try {
      setIsSigningOut(true)
      setAuthStatus('loading')

      await apis.userService.authLogoutPost()
    } catch {
      // Force logout even if API call fails
    } finally {
      clearUserInSentry()
      ClientAnalytics.reset()
      logout()
      setIsSigningOut(false)
      // Clear all React Query cache
      queryClient?.clear()
    }
  }, [logout, setIsSigningOut, setAuthStatus])

  const initializeAuth = useCallback(async () => {
    if (initializationRef.current) {
      return initializationRef.current
    }

    const initPromise = (async () => {
      if (authState.isAuthenticated) {
        return
      }
      try {
        setIsInitializing(true)
        setAuthStatus('loading')

        if (accessToken) {
          try {
            const { data } = await apis.userService.authValidateTokenPost()
            const { user, accessToken: newAccessToken, organizationType } = data
            if (user && newAccessToken) {
              setUser(user, organizationType)
              setAccessToken(newAccessToken)

              identifySession(user)

              return
            }
          } catch {
            setAccessToken(null)
          }
        }

        // Fallback to refresh token
        try {
          const { data } = await apis.userService.authRefreshPost()
          const { user, accessToken: newAccessToken, organizationType } = data
          if (user && newAccessToken) {
            setUser(user, organizationType)
            setAccessToken(newAccessToken)

            identifySession(user)
          }
        } catch {
          logout()
        }
      } catch {
        logout()
      } finally {
        setIsInitializing(false)
        initializationRef.current = null
      }
    })()

    initializationRef.current = initPromise
    return initPromise
  }, [setUser, setIsInitializing, setAuthStatus, logout, accessToken, setAccessToken])

  const openLoginDialog = useCallback(() => {
    setIsLoginDialogOpen(true)
    setIsSignupDialogOpen(false)
  }, [setIsLoginDialogOpen, setIsSignupDialogOpen])

  const openSignupDialog = useCallback(async (token?: string) => {
    setIsSignupDialogOpen(true)
    setIsLoginDialogOpen(false)
    // If invitation ID is provided, fetch invitation data or go to challenge acceptance 
    if (token) {
      try {
        setInvitationLoading(true)
        setInvitationData(null)
        setAssignmentToken(token) // Store the token for later use
        const { data: invitationDetails } = await apis.userService.invitationTokenGet(token)
        setInvitationData(invitationDetails)
        if (isLoggedIn && invitationDetails.isPublicAssignment) {
          setIsSignupDialogOpen(false)
          setIsAcceptChallengeDialogOpen(true)
        }
      } catch (error) {
        console.error('Failed to fetch invitation:', error)
        if (error instanceof AxiosError && error.response?.data?.errorCode === 'acceptedToken') {
          toast.error(t('errors.acceptedToken'), {
            duration: 8000,
            action: {
              label: t('common.login'),
              onClick: () => {
                openLoginDialog()
              }
            }
          })
        } else if (error instanceof AxiosError && error.response?.data?.errorCode === 'alreadyJoinedPublicAssignment') {
          // User already joined this assignment (e.g. backend handled it during OAuth) — just go to assignments
          navigate({ to: '/assignments' })
        } else {
          toast.error(t('errors.invalidInvitation'))
        }
        setInvitationData(null)
        setAssignmentToken(null)
        setIsSignupDialogOpen(false)
        if (error instanceof AxiosError && error.response?.data?.errorCode !== 'alreadyJoinedPublicAssignment') {
          navigate({ to: '/' })
        }
      } finally {
        setInvitationLoading(false)
      }
    } else {
      // Clear invitation data when no invitation ID
      setInvitationData(null)
      setAssignmentToken(null)
    }
  }, [setIsSignupDialogOpen, setIsLoginDialogOpen, setInvitationData, setInvitationLoading, setIsAcceptChallengeDialogOpen, setAssignmentToken, isLoggedIn])

  const closeAllDialogs = useCallback(() => {
    setIsLoginDialogOpen(false)
    setIsSignupDialogOpen(false)
    setIsAcceptChallengeDialogOpen(false)
    setInvitationData(null)
    setAssignmentToken(null)
  }, [setIsLoginDialogOpen, setIsSignupDialogOpen, setIsAcceptChallengeDialogOpen, setInvitationData, setAssignmentToken])

  const joinPublicAssignment = useCallback(async () => {
    if (!assignmentToken) {
      toast.error(t('errors.invalidAssignmentToken'))
      return false
    }

    try {
      setIsJoiningAssignment(true)
      await apis.userService.joinPublicAssignmentPost({ assignmentToken })
      toast.success(t('errors.assignmentJoinedSuccessfully'))
      setIsAcceptChallengeDialogOpen(false)
      setInvitationData(null)
      setAssignmentToken(null)
      navigate({ to: '/assignments' })
      return true
    } catch (error) {
      console.error('Failed to join public assignment:', error)
      if (error instanceof AxiosError && error.response?.data?.errorCode === 'alreadyJoinedPublicAssignment') {
        toast.error(t('errors.alreadyJoinedPublicAssignment'), {
          duration: 6000,
          action: {
            label: t('organizations.viewAssignments'),
            onClick: () => navigate({ to: '/assignments' }),
          },
        })
        setIsAcceptChallengeDialogOpen(false)
        setInvitationData(null)
        setAssignmentToken(null)
      } else if (error instanceof AxiosError && error.response?.data?.error) {
        toast.error(error.response.data.error)
      } else {
        toast.error(t('errors.failedToJoinAssignment'))
      }
      return false
    } finally {
      setIsJoiningAssignment(false)
    }
  }, [assignmentToken, setIsAcceptChallengeDialogOpen, setInvitationData, setAssignmentToken, navigate, t])

  // On initial load, if an assignment token was persisted in localStorage
  // during a social OAuth flow, resume the signup dialog and then clear it.
  // BUT: Don't auto-open if we're on an error page (error in URL params)
  useEffect(() => {
    const storedToken = window.localStorage.getItem('assignmentToken')
    if (!isInitializing || !storedToken || hasProcessedStoredAssignmentTokenRef.current) {
      return
    }

    // On OAuth callback pages, ProviderAuthHandler handles the assignment token flow.
    // Skip here to avoid racing with it.
    if (window.location.pathname.includes('/auth/')) {
      return
    }

    // Check if we're on an error page - if so, don't auto-open signup dialog
    const urlParams = new URLSearchParams(window.location.search)
    const hasError = urlParams.has('error') || urlParams.has('errorCode')
    
    if (hasError) {
      // Clear the token since we're showing an error instead
      window.localStorage.removeItem('assignmentToken')
      hasProcessedStoredAssignmentTokenRef.current = true
      return
    }

    const timeout = setTimeout(() => {
      hasProcessedStoredAssignmentTokenRef.current = true
      window.localStorage.removeItem('assignmentToken')
      navigate({ to: '/auth/invite', search: { token: storedToken } })
    }, 256)

    return () => clearTimeout(timeout)
  }, [navigate, isInitializing])

  // When user becomes logged in while there's a pending public assignment token,
  // open the AcceptChallengeDialog to join them. Covers email login, SSO, and any other method.
  useEffect(() => {
    if (isLoggedIn && assignmentToken && invitationData?.isPublicAssignment && !isAcceptChallengeDialogOpen) {
      setIsSignupDialogOpen(false)
      setIsLoginDialogOpen(false)
      setIsAcceptChallengeDialogOpen(true)
    }
  }, [isLoggedIn, assignmentToken, invitationData, isAcceptChallengeDialogOpen, setIsSignupDialogOpen, setIsLoginDialogOpen, setIsAcceptChallengeDialogOpen])

  const clearError = useCallback(() => {
    setError(null)
  }, [setError])

  const loggedInAction = useCallback(
    (source: string, action: () => void) => {
      if (authState.user) {
        action()
      } else {
        ClientAnalytics.track('loginCTA', { source })
        openLoginDialog()
      }
    },
    [authState.user, openLoginDialog]
  )

  const organizationType = useAtomValue(organizationTypeAtom)

  return {
    // State
    ...authState,
    error,
    errorCode,
    displayName,
    initials,
    isLoginDialogOpen,
    isSignupDialogOpen,
    isAcceptChallengeDialogOpen,
    invitationData,
    assignmentToken,
    loggedInAction,
    // Loading states
    isSigningInWithGoogle,
    isSigningInWithEmail,
    isRegistering,
    isSigningOut,
    isInitializing,
    invitationLoading,
    isJoiningAssignment,
    // Role checking
    isAdmin: isAdmin(authState.user),
    isPartner: isPartner(authState.user),
    isPerson: Boolean(authState.user?.personId),
    organizationId: authState.user?.organizationId,
    organizationType,
    // Actions
    signInWithGoogle,
    signInWithLinkedIn,
    signInWithEmail,
    register,
    signOut,
    initializeAuth,
    setIsLoginDialogOpen,
    setIsSignupDialogOpen,
    setIsAcceptChallengeDialogOpen,
    openLoginDialog,
    openSignupDialog,
    closeAllDialogs,
    closeLoginDialog: () => setIsLoginDialogOpen(false),
    closeSignupDialog: () => setIsSignupDialogOpen(false),
    closeAcceptChallengeDialog: () => {
      setIsAcceptChallengeDialogOpen(false)
      setAssignmentToken(null)
    },
    joinPublicAssignment,
    clearError,
  }
}
